import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "../../lib/cn";

export interface EmptyStateProps {
  icon?: LucideIcon;
  title: string;
  description?: string;
  action?: ReactNode;
  className?: string;
  /**
   * `default` (was the original) — pill-icon background + thin dashed
   * border. Affirmative empty-with-CTA surfaces (e.g. HomePage "no
   * teams yet").
   *
   * `dashed` — heavier dashed border, no pill background, icon sits
   * inline. The "no data yet" placeholder feel from v2; used on
   * surfaces where the empty state is *expected* (no conflicts, no
   * assignments published yet) rather than affirmative.
   */
  variant?: "default" | "dashed";
}

export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  className,
  variant = "default",
}: EmptyStateProps) {
  if (variant === "dashed") {
    return (
      <div
        className={cn(
          // #2172 — both variants rise in when they mount (`motion-state-in`).
          "motion-state-in flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-border bg-transparent px-6 py-10 text-center",
          className,
        )}
      >
        {Icon ? <Icon className="mb-2 h-5 w-5 text-fg-faint" aria-hidden="true" /> : null}
        <p className="text-sm font-medium text-fg">{title}</p>
        {description ? <p className="mt-1 max-w-md text-xs text-fg-subtle">{description}</p> : null}
        {action ? <div className="mt-3">{action}</div> : null}
      </div>
    );
  }
  return (
    <div
      className={cn(
        "motion-state-in flex flex-col items-center justify-center rounded-lg border border-dashed border-border bg-bg-elevated/40 px-6 py-12 text-center",
        className,
      )}
    >
      {Icon ? (
        <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-accent-soft text-accent-fg">
          <Icon className="h-5 w-5" aria-hidden="true" />
        </div>
      ) : null}
      <p className="text-base font-semibold text-fg">{title}</p>
      {description ? <p className="mt-1 max-w-md text-sm text-fg-subtle">{description}</p> : null}
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  );
}
